import { useMemo } from 'react';
import { useQuery, request } from '@applyboard/ui-utils';
import { z } from 'zod';

const countriesResponseSchema = z.object({
  data: z.array(
    z.object({
      id: z.string(),
      attributes: z.object({
        code: z.string(),
        name: z.string(),
      }),
    }),
  ),
});

export interface Country {
  code: string;
  name: string;
}

export function useCountries() {
  const { isSuccess, isLoading, isFetching, error, data } = useQuery(
    ['countries', { pkg: 'ab-student-intake-form' }],
    async () => {
      const response = await request('/api/v2/countries?sort=name', {
        schema: countriesResponseSchema,
      });

      return response.data.map<Country>(dataItem => ({
        id: dataItem.id,
        code: dataItem.attributes.code,
        name: dataItem.attributes.name,
      }));
    },
  );

  const countryToCodeMapping = useMemo(() => {
    if (!data) {
      return null;
    }

    return data.reduce(
      (acc, curr) => {
        acc[curr.code] = curr.name;
        return acc;
      },
      {} as { [countryCode: string]: string },
    );
  }, [data]);

  return {
    isLoadingCountries: isLoading,
    isFetchingCountries: isFetching,
    countriesError: error,
    countries: isSuccess ? data : [],
    countryToCodeMapping,
  };
}
